import { useEffect } from 'react';
import styled from '@emotion/styled';

import { Drawer, Portal } from '@common/overlay-manager';
import { ThemeProvider } from '@common/styles/theme-provider';

import { BaseLayout } from '../component/layout/BaseLayout';
import { Loader } from '../component/loader/Loader';
import { RestockNotificationPopup } from '../component/popup/RestockNotificationPopup';
import { ComingSoon } from '../component/teasing/ComingSoon';
import { useProductOptionContext } from '../context/ProductOptionProvider';
import { useOptions } from '../query/useOptions';
import { useProductOptionService } from '../service/useProductOptionService';
import type { ProductOptionProps } from '../type/common.type';
import { ItemCardsSection } from './sections/ItemCardsSection';
import { NoticeTextSection } from './sections/NoticeTextSection';
import { AdditionalOptionsSection } from './sections/option/AdditionalOptionsSection';
import { CustomInputSection } from './sections/option/CustomInputSection';
import { MainOptionsSection } from './sections/option/MainOptionsSection';
import { ProductOptionFooterSection } from './sections/ProductOptionFooterSection';
import { ProductOptionHeaderSection } from './sections/ProductOptionHeaderSection';

export const ProductOptionContainer = (props: ProductOptionProps) => {
  const { use_drawer = true, open = false, productId, preloadedOptionData, context, config, onStateChange } = props;
  const { options, isFetched } = useOptions(productId, context?.entrySourceType);
  const { state } = useProductOptionContext();
  const {
    restockNotificationPopup,

    header,
    subHeader,
    customInput,
    mainOptions,
    additionalOptions,
    itemCards,
    noticeText,
    footer,
    teasing,
    initialize,
    handleClose,
  } = useProductOptionService(props);

  const enabledLoadingIndicator = config?.enabledLoadingIndicator ?? true;
  const isOpen = use_drawer ? open : true;

  useEffect(() => {
    if (isFetched && options && isOpen) {
      initialize(preloadedOptionData ?? options);
    }
  }, [isFetched, options, isOpen]);

  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  // enabledLoadingIndicator가 false이거나 isFetched가 true일 때만 컨텐츠 표시
  const shouldShowContent = !enabledLoadingIndicator || isFetched;

  const content = shouldShowContent ? (
    <ContentContainer>
      {/* 직진 배송 알림 팝업 */}
      <RestockNotificationPopup
        status={restockNotificationPopup.status}
        onClose={restockNotificationPopup.actions.onClose}
      />

      {config?.slots?.HeaderContent}

      <BaseLayout>
        {/* 직진 배송 알림 타이틀, 직진 토글 버튼 */}
        <ProductOptionHeaderSection headerTitle={header.headerTitle} shippingFilterToggle={header.shippingFilter} />
        {typeof config?.slots?.SubHeaderContent === 'function'
          ? config.slots.SubHeaderContent(subHeader)
          : config?.slots?.SubHeaderContent}

        <BaseLayout.ScrollArea>
          {/* 입력형 옵션 */}
          <CustomInputSection {...customInput} />

          {/* 기본 옵션 */}
          <MainOptionsSection {...mainOptions} />

          {/* 추가 옵션 */}
          <AdditionalOptionsSection {...additionalOptions} />

          {/* 아이템 카드 */}
          <ItemCardsSection {...itemCards} />

          {config?.slots?.NoticeTextTopContent}

          {/* 알림 문구 */}
          <NoticeTextSection {...noticeText} />

          {config?.slots?.NoticeTextBottomContent}
        </BaseLayout.ScrollArea>

        {/* 가격, 버튼 */}
        <ProductOptionFooterSection {...footer} />
      </BaseLayout>

      <ComingSoon {...teasing} />
    </ContentContainer>
  ) : (
    <Loader />
  );

  if (use_drawer) {
    return (
      <Portal>
        <ThemeProvider colorScheme='light'>
          <Drawer open={open} onClose={handleClose} maxHeight={'calc(100vh - 16px)'} height='65vh' overflow='visible'>
            {content}
          </Drawer>
        </ThemeProvider>
      </Portal>
    );
  }

  return content;
};

const ContentContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  backface-visibility: hidden;
`;
